import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentAssistantProposal,
  AgentAutomationResourceDraftPayload,
  AgentDraftContext,
  AgentIntegrationResourceDraftPayload,
  AgentResourceDraft,
  AgentScheduleConfig,
  AgentWorkspace,
} from '@servicesai/api-client';
import {
  EmptyState,
  GlassButton,
  GlassCard,
  GlassInput,
  GlassSelect,
  GlassTextarea,
  StatusPill,
  useI18n,
} from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import { S } from '../../strings.js';

interface CatalogItem {
  key: string;
  label: string;
  description: string;
  risk: 'low' | 'standard' | 'elevated' | 'critical';
  capabilities: string[];
  allowedConfigKeys: string[];
  secretRefOptional: boolean;
}

interface ResourcePanelProps {
  slug: string;
  workspace: AgentWorkspace;
  selectedNoteIds?: string[];
}

type IntegrationDraft = Extract<AgentResourceDraft, { kind: 'integration' }>;
type AutomationDraft = Extract<AgentResourceDraft, { kind: 'automation' }>;

const EMPTY_CONTEXT: AgentDraftContext = {
  objective: '',
  rationale: '',
  expectedOutcome: '',
  constraints: [],
  successCriteria: [],
  ownerId: null,
  noteRefs: [],
};

const EMPTY_INTEGRATION: AgentIntegrationResourceDraftPayload = {
  name: '',
  desiredCapabilities: [],
  dataNeeded: [],
  actionsNeeded: [],
};

const EMPTY_AUTOMATION: AgentAutomationResourceDraftPayload = {
  name: '',
};

const EMPTY_SCHEDULE: AgentScheduleConfig = {
  name: '',
  purpose: 'agent_run',
  enabled: true,
  cron: '0 6 * * *',
  timezone: 'Asia/Dubai',
  conditions: {},
  audience: { type: 'all', values: [] },
  channel: null,
  templateKey: null,
  quietHours: { enabled: false, start: '22:00', end: '07:00' },
  cooldownMinutes: 0,
  retryPolicy: { maxAttempts: 3, backoffMinutes: 5 },
  reportDefinitionId: null,
};

function parseObject(value: string, label: string): Record<string, unknown> {
  const parsed = JSON.parse(value || '{}') as unknown;
  if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
    throw new Error(`${label} must be a JSON object`);
  }
  return parsed as Record<string, unknown>;
}

function list(value: string): string[] {
  return [...new Set(value.split(/[\n,]/u).map((item) => item.trim()).filter(Boolean))];
}

function contextComplete(context: AgentDraftContext): boolean {
  return Boolean(
    context.objective.trim()
    && context.rationale.trim()
    && context.expectedOutcome.trim()
    && context.successCriteria.length,
  );
}

function lifecycleState(lifecycle: AgentResourceDraft['lifecycle']) {
  if (lifecycle === 'verified' || lifecycle === 'promoted') return 'published';
  if (lifecycle === 'mapped') return 'in_review';
  if (lifecycle === 'archived') return 'rejected';
  return 'draft';
}

function hasTechnicalMapping(draft: AgentResourceDraft): boolean {
  return draft.kind === 'report'
    ? draft.payload.definition !== undefined
    : draft.payload.mapping !== undefined;
}

export function ContextFields({
  value,
  onChange,
}: {
  value: AgentDraftContext;
  onChange: (value: AgentDraftContext) => void;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  return (
    <fieldset className="rounded-xl border border-aeblack-100 bg-aeblack-50 p-4">
      <legend className="px-1 text-sm font-extrabold">{text('Draft context', 'سياق المسودة')}</legend>
      <p className="mb-3 text-xs text-ink-faint">
        {text('Capture why this resource exists before technical configuration.', 'سجّل سبب وجود هذا المورد قبل الإعداد التقني.')}
      </p>
      <div className="grid gap-3 lg:grid-cols-2">
        <label className="text-xs font-bold text-ink-soft">
          {text('Objective', 'الهدف')}
          <GlassTextarea rows={3} value={value.objective} onChange={(event) => onChange({ ...value, objective: event.target.value })} className="mt-1" />
        </label>
        <label className="text-xs font-bold text-ink-soft">
          {text('Rationale', 'المبررات')}
          <GlassTextarea rows={3} value={value.rationale} onChange={(event) => onChange({ ...value, rationale: event.target.value })} className="mt-1" />
        </label>
        <label className="text-xs font-bold text-ink-soft lg:col-span-2">
          {text('Expected outcome', 'النتيجة المتوقعة')}
          <GlassTextarea rows={3} value={value.expectedOutcome} onChange={(event) => onChange({ ...value, expectedOutcome: event.target.value })} className="mt-1" />
        </label>
        <label className="text-xs font-bold text-ink-soft">
          {text('Constraints', 'القيود')}
          <GlassTextarea
            rows={3}
            value={value.constraints.join('\n')}
            onChange={(event) => onChange({ ...value, constraints: list(event.target.value) })}
            className="mt-1"
            placeholder={text('One per line', 'قيد واحد في كل سطر')}
          />
        </label>
        <label className="text-xs font-bold text-ink-soft">
          {text('Success criteria', 'معايير النجاح')}
          <GlassTextarea
            rows={3}
            value={value.successCriteria.join('\n')}
            onChange={(event) => onChange({ ...value, successCriteria: list(event.target.value) })}
            className="mt-1"
            placeholder={text('One per line', 'معيار واحد في كل سطر')}
          />
        </label>
      </div>
      {value.noteRefs.length > 0 && (
        <p className="mt-3 text-[10px] text-ink-faint">
          {text('Attached note revisions', 'إصدارات الملاحظات المرفقة')}: {value.noteRefs.map((note) => `${note.id.slice(0, 8)}@r${note.revision}`).join(', ')}
        </p>
      )}
    </fieldset>
  );
}

function PublishedResources({
  kind,
  workspace,
}: {
  kind: 'integration' | 'automation';
  workspace: AgentWorkspace;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const resources = kind === 'integration'
    ? workspace.effectiveConfig.integrations
    : workspace.effectiveConfig.schedules;
  return (
    <GlassCard className="p-5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-extrabold">{text('Published configuration', 'الإعداد المنشور')}</h3>
          <p className="mt-1 text-xs text-ink-faint">{text('Only promoted and approved resources can execute.', 'لا يمكن التنفيذ إلا للموارد المرقاة والمعتمدة.')}</p>
        </div>
        <StatusPill state="published" label={String(resources.length)} />
      </div>
      {resources.length === 0 ? (
        <div className="mt-3 text-xs text-ink-faint">{text('No published resources.', 'لا توجد موارد منشورة.')}</div>
      ) : (
        <div className="mt-4 grid gap-2 lg:grid-cols-2">
          {resources.map((resource) => (
            <div key={resource.id ?? ('adapterKey' in resource ? resource.adapterKey : resource.name)} className="rounded-lg border border-aeblack-100 bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-bold">{'displayName' in resource ? resource.displayName : resource.name}</div>
                  <code className="text-[10px] text-ink-faint">
                    {'adapterKey' in resource ? resource.adapterKey : `${resource.cron} · ${resource.timezone}`}
                  </code>
                </div>
                <StatusPill state={resource.enabled ? 'published' : 'draft'} label={resource.enabled ? text('Enabled', 'مفعّل') : text('Disabled', 'معطّل')} />
              </div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

export function ResourceGuidance({
  slug,
  draft,
  workspace,
  selectedNoteIds,
  onApplied,
}: {
  slug: string;
  draft: AgentResourceDraft;
  workspace: AgentWorkspace;
  selectedNoteIds: string[];
  onApplied: () => Promise<void>;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const [message, setMessage] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [proposal, setProposal] = useState<AgentAssistantProposal | null>(null);
  const noteRefs = workspace.notes
    .filter((note) => selectedNoteIds.includes(note.id) && !note.archived_at)
    .map((note) => ({ id: note.id, revision: note.revision }));
  const propose = useMutation({
    mutationFn: async () => {
      let id = sessionId;
      if (!id) {
        const created = await admin().createAgentAssistantSession(
          slug,
          { kind: 'resource_draft', resourceDraftId: draft.id },
          { paths: ['/context', '/payload'], noteRefs },
        );
        id = created.session.id;
        setSessionId(id);
      }
      return admin().proposeAgentConfiguration(slug, id, {
        message: message.trim(),
        noteIds: selectedNoteIds,
        lang,
      });
    },
    onSuccess: ({ proposal: next }) => {
      setProposal(next);
      setMessage('');
    },
  });
  const apply = useMutation({
    mutationFn: () => {
      if (!sessionId || !proposal) throw new Error('Proposal is unavailable');
      return admin().applyAgentConfigurationProposal(
        slug,
        sessionId,
        proposal.id,
        { expectedRevision: draft.revision },
      );
    },
    onSuccess: async () => {
      setProposal(null);
      await onApplied();
    },
  });
  const reject = useMutation({
    mutationFn: () => {
      if (!sessionId || !proposal) throw new Error('Proposal is unavailable');
      return admin().rejectAgentConfigurationProposal(slug, sessionId, proposal.id);
    },
    onSuccess: () => setProposal(null),
  });
  const error = propose.error ?? apply.error ?? reject.error;
  const busy = propose.isPending || apply.isPending || reject.isPending;
  return (
    <div className="rounded-xl border border-aegold-200 bg-aegold-50 p-4">
      <div className="text-sm font-extrabold">{text('Configure with AI guidance', 'الإعداد بإرشاد الذكاء الاصطناعي')}</div>
      <p className="mt-1 text-xs text-ink-soft">
        {text('Continue a scoped conversation. AI can only propose context and payload changes.', 'تابع محادثة محددة النطاق. يمكن للذكاء الاصطناعي اقتراح تغييرات على السياق والمحتوى فقط.')}
      </p>
      <GlassTextarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        rows={4}
        maxLength={20_000}
        className="mt-3"
        placeholder={text('Describe what you need, answer a question, or refine the draft…', 'صف ما تحتاجه أو أجب عن سؤال أو حسّن المسودة…')}
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] text-ink-faint">{noteRefs.length} {text('note revisions in scope', 'إصدارات ملاحظات ضمن النطاق')}</span>
        <GlassButton size="sm" variant="primary" disabled={busy || !message.trim()} onClick={() => propose.mutate()}>
          {propose.isPending ? text('Thinking…', 'جارٍ التفكير…') : text('Continue guidance', 'متابعة الإرشاد')}
        </GlassButton>
      </div>
      {error && <div className="aegov-alert alert-error mt-3" role="alert"><div className="alert-content text-xs">{(error as Error).message}</div></div>}
      {proposal && (
        <div className="mt-3 rounded-lg border border-aeblack-100 bg-white p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-sm font-bold">{proposal.summary}</div>
              <p className="mt-1 text-xs text-ink-soft">{proposal.rationale}</p>
            </div>
            <StatusPill state={proposal.validation.valid ? 'published' : 'rejected'} label={proposal.validation.valid ? text('Valid', 'صالح') : text('Invalid', 'غير صالح')} />
          </div>
          {proposal.questions.length > 0 && (
            <ul className="mt-2 list-disc space-y-1 ps-5 text-xs text-ink-soft">
              {proposal.questions.map((question) => <li key={question}>{question}</li>)}
            </ul>
          )}
          {proposal.validation.warnings.length > 0 && (
            <div className="mt-2 rounded bg-aeblack-50 p-2 text-[10px] text-ink-faint">
              {proposal.validation.warnings.map((warning) => <div key={`${warning.path}-${warning.message}`}>{warning.path}: {warning.message}</div>)}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <GlassButton
              size="sm"
              variant="primary"
              disabled={busy || !proposal.validation.valid || proposal.patch.length === 0}
              onClick={() => apply.mutate()}
            >
              {text('Apply to draft', 'تطبيق على المسودة')}
            </GlassButton>
            <GlassButton size="sm" variant="danger" disabled={busy} onClick={() => reject.mutate()}>
              {text('Reject', 'رفض')}
            </GlassButton>
          </div>
        </div>
      )}
    </div>
  );
}

function DraftCard({
  draft,
  busy,
  onEdit,
  onVerify,
  onPromote,
  onArchive,
}: {
  draft: AgentResourceDraft;
  busy: boolean;
  onEdit: () => void;
  onVerify: () => void;
  onPromote: () => void;
  onArchive: () => void;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const name = draft.payload.name;
  const missing = [
    !draft.context.objective.trim() && text('objective', 'الهدف'),
    !draft.context.rationale.trim() && text('rationale', 'المبررات'),
    !draft.context.expectedOutcome.trim() && text('expected outcome', 'النتيجة المتوقعة'),
    draft.context.successCriteria.length === 0 && text('success criteria', 'معايير النجاح'),
    !hasTechnicalMapping(draft) && text('technical mapping', 'الربط التقني'),
  ].filter(Boolean) as string[];
  return (
    <GlassCard className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-extrabold">{name}</div>
          <p className="mt-1 line-clamp-2 text-xs text-ink-soft">{draft.context.objective || text('Intent captured; objective not yet defined.', 'تم حفظ الفكرة؛ لم يُحدد الهدف بعد.')}</p>
        </div>
        <StatusPill state={lifecycleState(draft.lifecycle)} label={draft.lifecycle} />
      </div>
      <div className="mt-3 grid gap-2 border-t border-aeblack-100 pt-3 text-[10px] sm:grid-cols-3">
        <div><span className="text-ink-faint">{text('Revision', 'الإصدار')}</span><div className="font-bold">r{draft.revision}</div></div>
        <div><span className="text-ink-faint">{text('Verification', 'التحقق')}</span><div className="font-bold">{draft.verification.status}</div></div>
        <div><span className="text-ink-faint">{text('Missing', 'الناقص')}</span><div className="font-bold">{missing.length}</div></div>
      </div>
      {missing.length > 0 && <p className="mt-2 text-[10px] text-ink-faint">{text('Still needed', 'لا يزال مطلوباً')}: {missing.join(', ')}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {draft.lifecycle !== 'promoted' && draft.lifecycle !== 'archived' && (
          <>
            <GlassButton size="sm" disabled={busy} onClick={onEdit}>{text('Edit', 'تعديل')}</GlassButton>
            <GlassButton size="sm" disabled={busy || !hasTechnicalMapping(draft)} onClick={onVerify}>
              {text('Verify', 'تحقق')}
            </GlassButton>
            <GlassButton size="sm" variant="primary" disabled={busy || draft.lifecycle !== 'verified' || !contextComplete(draft.context)} onClick={onPromote}>
              {text('Promote', 'ترقية')}
            </GlassButton>
            <GlassButton size="sm" variant="danger" disabled={busy} onClick={onArchive}>{text('Archive', 'أرشفة')}</GlassButton>
          </>
        )}
      </div>
    </GlassCard>
  );
}

function useResourceActions(slug: string) {
  const queryClient = useQueryClient();
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] }),
      queryClient.invalidateQueries({ queryKey: ['agent-detail', slug] }),
    ]);
  };
  const verify = useMutation({
    mutationFn: (draft: AgentResourceDraft) =>
      admin().verifyAgentResourceDraft(slug, draft.id, { expectedRevision: draft.revision }),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (draft: AgentResourceDraft) =>
      admin().archiveAgentResourceDraft(slug, draft.id, draft.revision),
    onSuccess: refresh,
  });
  const promote = useMutation({
    mutationFn: ({ draft, workspace }: { draft: AgentResourceDraft; workspace: AgentWorkspace }) => {
      const configDraft = workspace.versions.find((version) => version.state === 'draft');
      return admin().promoteAgentResourceDraft(slug, draft.id, {
        expectedRevision: draft.revision,
        ...(configDraft
          ? { configVersionId: configDraft.id, expectedConfigLockVersion: configDraft.lock_version }
          : {}),
      });
    },
    onSuccess: refresh,
  });
  return {
    refresh,
    verify,
    archive,
    promote,
    busy: verify.isPending || archive.isPending || promote.isPending,
    error: verify.error ?? archive.error ?? promote.error,
  };
}

export function AgentIntegrationsPanel({
  slug,
  workspace,
  selectedNoteIds = [],
}: ResourcePanelProps) {
  const { l, lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const queryClient = useQueryClient();
  const drafts = workspace.resourceDrafts.filter((draft): draft is IntegrationDraft => draft.kind === 'integration');
  const catalogQuery = useQuery({
    queryKey: ['agent-integration-catalog', slug],
    queryFn: () => admin().agentIntegrationCatalog(slug),
  });
  const catalog = (catalogQuery.data?.items ?? []) as CatalogItem[];
  const [editing, setEditing] = useState<IntegrationDraft | null>(null);
  const [payload, setPayload] = useState<AgentIntegrationResourceDraftPayload>(EMPTY_INTEGRATION);
  const [context, setContext] = useState<AgentDraftContext>(EMPTY_CONTEXT);
  const [showMapping, setShowMapping] = useState(false);
  const [scopesText, setScopesText] = useState('{}');
  const [configText, setConfigText] = useState('{}');
  const [formError, setFormError] = useState<string | null>(null);
  const actions = useResourceActions(slug);
  const selectedNotes = useMemo(
    () => workspace.notes
      .filter((note) => selectedNoteIds.includes(note.id) && !note.archived_at)
      .map((note) => ({ id: note.id, revision: note.revision })),
    [selectedNoteIds, workspace.notes],
  );
  const save = useMutation({
    mutationFn: () => {
      if (!payload.name.trim()) throw new Error(text('A draft name is required.', 'اسم المسودة مطلوب.'));
      const input = {
        kind: 'integration' as const,
        context: { ...context, noteRefs: selectedNotes },
        payload: {
          ...payload,
          name: payload.name.trim(),
          ...(payload.mapping
            ? {
                mapping: {
                  ...payload.mapping,
                  displayName: payload.mapping.displayName.trim(),
                  scopes: parseObject(scopesText, 'Scopes'),
                  config: parseObject(configText, 'Configuration'),
                  secretRef: payload.mapping.secretRef?.trim() || null,
                },
              }
            : {}),
        },
      };
      return editing
        ? admin().updateAgentResourceDraft(slug, editing.id, { ...input, expectedRevision: editing.revision })
        : admin().createAgentResourceDraft(slug, input);
    },
    onSuccess: async () => {
      setEditing(null);
      setPayload(EMPTY_INTEGRATION);
      setContext(EMPTY_CONTEXT);
      setShowMapping(false);
      setScopesText('{}');
      setConfigText('{}');
      setFormError(null);
      await queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] });
    },
    onError: (error) => setFormError((error as Error).message),
  });
  const beginEdit = (draft: IntegrationDraft) => {
    setEditing(draft);
    setPayload(draft.payload);
    setContext(draft.context);
    setShowMapping(Boolean(draft.payload.mapping));
    setScopesText(JSON.stringify(draft.payload.mapping?.scopes ?? {}, null, 2));
    setConfigText(JSON.stringify(draft.payload.mapping?.config ?? {}, null, 2));
    setFormError(null);
  };
  const chooseAdapter = (adapterKey: string) => {
    const manifest = catalog.find((item) => item.key === adapterKey);
    setPayload({
      ...payload,
      mapping: adapterKey
        ? {
            adapterKey,
            displayName: manifest?.label ?? adapterKey,
            enabled: true,
            capabilities: manifest?.capabilities ?? [],
            scopes: {},
            secretRef: null,
            config: {},
          }
        : undefined,
    });
  };
  const busy = save.isPending || actions.busy;
  return (
    <div className="space-y-4">
      <GlassCard className="p-5" strong>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold">{l(S.agentIntegrations)}</h2>
            <p className="mt-1 max-w-3xl text-xs text-ink-faint">{text('Start with intent and context. Technical mapping is optional until verification.', 'ابدأ بالهدف والسياق. الربط التقني اختياري حتى مرحلة التحقق.')}</p>
          </div>
          <StatusPill state="in_progress" label={`${drafts.length} ${text('workspace drafts', 'مسودات مساحة العمل')}`} />
        </div>
      </GlassCard>
      {(actions.error || formError) && <div className="aegov-alert alert-error" role="alert"><div className="alert-content">{formError ?? (actions.error as Error).message}</div></div>}
      <PublishedResources kind="integration" workspace={workspace} />
      <div>
        <h3 className="mb-3 text-base font-extrabold">{text('Intent-first drafts', 'مسودات تبدأ بالهدف')}</h3>
        {drafts.length === 0 ? (
          <GlassCard><EmptyState title={text('No integration concepts yet', 'لا توجد أفكار تكامل بعد')} hint={text('Save a name now; add technical fields later.', 'احفظ اسماً الآن وأضف الحقول التقنية لاحقاً.')} /></GlassCard>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {drafts.map((draft) => (
              <DraftCard
                key={draft.id}
                draft={draft}
                busy={busy}
                onEdit={() => beginEdit(draft)}
                onVerify={() => actions.verify.mutate(draft)}
                onPromote={() => actions.promote.mutate({ draft, workspace })}
                onArchive={() => actions.archive.mutate(draft)}
              />
            ))}
          </div>
        )}
      </div>
      <GlassCard className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-extrabold">{editing ? text('Edit integration draft', 'تعديل مسودة التكامل') : text('New integration concept', 'فكرة تكامل جديدة')}</h3>
            <p className="mt-1 text-xs text-ink-faint">{text('Only the name is required to save.', 'الاسم فقط مطلوب للحفظ.')}</p>
          </div>
          {editing && <GlassButton size="sm" onClick={() => {
            setEditing(null);
            setPayload(EMPTY_INTEGRATION);
            setContext(EMPTY_CONTEXT);
          }}>{l(S.cancel)}</GlassButton>}
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          <label className="text-xs font-bold text-ink-soft">
            {text('Draft name', 'اسم المسودة')}
            <GlassInput value={payload.name} onChange={(event) => setPayload({ ...payload, name: event.target.value })} className="mt-1" />
          </label>
          <label className="text-xs font-bold text-ink-soft">
            {text('Desired system', 'النظام المطلوب')}
            <GlassInput value={payload.desiredSystem ?? ''} onChange={(event) => setPayload({ ...payload, desiredSystem: event.target.value || undefined })} className="mt-1" />
          </label>
          <label className="text-xs font-bold text-ink-soft lg:col-span-2">
            {text('Purpose', 'الغرض')}
            <GlassTextarea rows={3} value={payload.purpose ?? ''} onChange={(event) => setPayload({ ...payload, purpose: event.target.value || undefined })} className="mt-1" />
          </label>
          {([
            ['desiredCapabilities', text('Desired capabilities', 'القدرات المطلوبة')],
            ['dataNeeded', text('Data needed', 'البيانات المطلوبة')],
            ['actionsNeeded', text('Actions needed', 'الإجراءات المطلوبة')],
          ] as const).map(([key, label]) => (
            <label key={key} className="text-xs font-bold text-ink-soft">
              {label}
              <GlassTextarea rows={3} value={(payload[key] ?? []).join('\n')} onChange={(event) => setPayload({ ...payload, [key]: list(event.target.value) })} className="mt-1" />
            </label>
          ))}
          <label className="text-xs font-bold text-ink-soft">
            {text('Custom connector concept', 'فكرة موصل مخصص')}
            <GlassTextarea rows={3} value={payload.customConnectorDescription ?? ''} onChange={(event) => setPayload({ ...payload, customConnectorDescription: event.target.value || undefined })} className="mt-1" />
          </label>
        </div>
        <div className="mt-4"><ContextFields value={context} onChange={setContext} /></div>
        <div className="mt-4 rounded-xl border border-aeblack-100 p-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={showMapping} onChange={(event) => {
              setShowMapping(event.target.checked);
              if (!event.target.checked) setPayload({ ...payload, mapping: undefined });
            }} />
            {text('Add technical mapping now', 'إضافة الربط التقني الآن')}
          </label>
          {showMapping && (
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              <label className="text-xs font-bold text-ink-soft">
                {l(S.catalogAdapter)}
                <GlassSelect value={payload.mapping?.adapterKey ?? ''} onChange={(event) => chooseAdapter(event.target.value)} className="mt-1">
                  <option value="">{l(S.chooseAdapter)}</option>
                  {catalog.map((item) => <option key={item.key} value={item.key}>{item.label} · {item.risk}</option>)}
                </GlassSelect>
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.displayName)}
                <GlassInput value={payload.mapping?.displayName ?? ''} onChange={(event) => payload.mapping && setPayload({ ...payload, mapping: { ...payload.mapping, displayName: event.target.value } })} className="mt-1" />
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.secretReference)}
                <GlassInput value={payload.mapping?.secretRef ?? ''} onChange={(event) => payload.mapping && setPayload({ ...payload, mapping: { ...payload.mapping, secretRef: event.target.value || null } })} className="mt-1" dir="ltr" placeholder="keyvault://…" />
              </label>
              <div className="text-xs font-bold text-ink-soft">
                {l(S.capabilities)}
                <div className="mt-1 flex min-h-10 flex-wrap gap-2 rounded-lg border border-aeblack-100 p-2">
                  {(catalog.find((item) => item.key === payload.mapping?.adapterKey)?.capabilities ?? []).map((capability) => (
                    <label key={capability} className="flex items-center gap-1.5 rounded bg-aeblack-50 px-2 py-1 text-[10px]">
                      <input
                        type="checkbox"
                        checked={payload.mapping?.capabilities.includes(capability) ?? false}
                        onChange={(event) => payload.mapping && setPayload({
                          ...payload,
                          mapping: {
                            ...payload.mapping,
                            capabilities: event.target.checked
                              ? [...payload.mapping.capabilities, capability]
                              : payload.mapping.capabilities.filter((item) => item !== capability),
                          },
                        })}
                      />
                      {capability}
                    </label>
                  ))}
                </div>
              </div>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.scopesJson)}
                <GlassTextarea rows={6} value={scopesText} onChange={(event) => setScopesText(event.target.value)} className="mt-1 font-mono" dir="ltr" />
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.configurationJson)}
                <GlassTextarea rows={6} value={configText} onChange={(event) => setConfigText(event.target.value)} className="mt-1 font-mono" dir="ltr" />
              </label>
            </div>
          )}
        </div>
        {editing && (
          <div className="mt-4">
            <ResourceGuidance slug={slug} draft={editing} workspace={workspace} selectedNoteIds={selectedNoteIds} onApplied={actions.refresh} />
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <GlassButton variant="primary" disabled={save.isPending || !payload.name.trim()} onClick={() => save.mutate()}>
            {save.isPending ? l(S.saving) : text('Save draft', 'حفظ المسودة')}
          </GlassButton>
        </div>
      </GlassCard>
    </div>
  );
}

export function AgentAutomationsPanel({
  slug,
  workspace,
  selectedNoteIds = [],
}: ResourcePanelProps) {
  const { l, lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const queryClient = useQueryClient();
  const drafts = workspace.resourceDrafts.filter((draft): draft is AutomationDraft => draft.kind === 'automation');
  const [editing, setEditing] = useState<AutomationDraft | null>(null);
  const [payload, setPayload] = useState<AgentAutomationResourceDraftPayload>(EMPTY_AUTOMATION);
  const [context, setContext] = useState<AgentDraftContext>(EMPTY_CONTEXT);
  const [showMapping, setShowMapping] = useState(false);
  const [schedule, setSchedule] = useState<AgentScheduleConfig>(EMPTY_SCHEDULE);
  const [conditionsText, setConditionsText] = useState('{}');
  const [audienceValues, setAudienceValues] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string[]>([]);
  const actions = useResourceActions(slug);
  const selectedNotes = useMemo(
    () => workspace.notes
      .filter((note) => selectedNoteIds.includes(note.id) && !note.archived_at)
      .map((note) => ({ id: note.id, revision: note.revision })),
    [selectedNoteIds, workspace.notes],
  );
  const previewMutation = useMutation({
    mutationFn: () => admin().previewAgentSchedule(slug, schedule.cron, schedule.timezone, 5),
    onSuccess: (result) => setPreview(result.occurrences),
    onError: (error) => setFormError((error as Error).message),
  });
  const save = useMutation({
    mutationFn: () => {
      if (!payload.name.trim()) throw new Error(text('A draft name is required.', 'اسم المسودة مطلوب.'));
      const input = {
        kind: 'automation' as const,
        context: { ...context, noteRefs: selectedNotes },
        payload: {
          ...payload,
          name: payload.name.trim(),
          ...(showMapping
            ? {
                mapping: {
                  ...schedule,
                  name: schedule.name.trim() || payload.name.trim(),
                  cron: schedule.cron.trim(),
                  timezone: schedule.timezone.trim(),
                  conditions: parseObject(conditionsText, 'Conditions'),
                  audience: { ...schedule.audience, values: list(audienceValues) },
                  templateKey: schedule.templateKey?.trim() || null,
                },
              }
            : { mapping: undefined }),
        },
      };
      return editing
        ? admin().updateAgentResourceDraft(slug, editing.id, { ...input, expectedRevision: editing.revision })
        : admin().createAgentResourceDraft(slug, input);
    },
    onSuccess: async () => {
      setEditing(null);
      setPayload(EMPTY_AUTOMATION);
      setContext(EMPTY_CONTEXT);
      setShowMapping(false);
      setSchedule(EMPTY_SCHEDULE);
      setConditionsText('{}');
      setAudienceValues('');
      setPreview([]);
      setFormError(null);
      await queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] });
    },
    onError: (error) => setFormError((error as Error).message),
  });
  const beginEdit = (draft: AutomationDraft) => {
    setEditing(draft);
    setPayload(draft.payload);
    setContext(draft.context);
    setShowMapping(Boolean(draft.payload.mapping));
    setSchedule(draft.payload.mapping ?? { ...EMPTY_SCHEDULE, name: draft.payload.name });
    setConditionsText(JSON.stringify(draft.payload.mapping?.conditions ?? {}, null, 2));
    setAudienceValues(draft.payload.mapping?.audience.values.join('\n') ?? '');
    setFormError(null);
  };
  const busy = save.isPending || actions.busy;
  return (
    <div className="space-y-4">
      <GlassCard className="p-5" strong>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold">{l(S.automations)}</h2>
            <p className="mt-1 max-w-3xl text-xs text-ink-faint">{text('Describe trigger, action, and outcome first. Add cron and delivery later.', 'صف المحفز والإجراء والنتيجة أولاً، ثم أضف الجدولة والتسليم لاحقاً.')}</p>
          </div>
          <StatusPill state="in_progress" label={`${drafts.length} ${text('workspace drafts', 'مسودات مساحة العمل')}`} />
        </div>
      </GlassCard>
      {(actions.error || formError) && <div className="aegov-alert alert-error" role="alert"><div className="alert-content">{formError ?? (actions.error as Error).message}</div></div>}
      <PublishedResources kind="automation" workspace={workspace} />
      <div>
        <h3 className="mb-3 text-base font-extrabold">{text('Intent-first drafts', 'مسودات تبدأ بالهدف')}</h3>
        {drafts.length === 0 ? (
          <GlassCard><EmptyState title={text('No automation concepts yet', 'لا توجد أفكار أتمتة بعد')} hint={text('Save a name now; define the schedule later.', 'احفظ اسماً الآن وحدد الجدول لاحقاً.')} /></GlassCard>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {drafts.map((draft) => (
              <DraftCard
                key={draft.id}
                draft={draft}
                busy={busy}
                onEdit={() => beginEdit(draft)}
                onVerify={() => actions.verify.mutate(draft)}
                onPromote={() => actions.promote.mutate({ draft, workspace })}
                onArchive={() => actions.archive.mutate(draft)}
              />
            ))}
          </div>
        )}
      </div>
      <GlassCard className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-extrabold">{editing ? text('Edit automation draft', 'تعديل مسودة الأتمتة') : text('New automation concept', 'فكرة أتمتة جديدة')}</h3>
            <p className="mt-1 text-xs text-ink-faint">{text('Only the name is required to save.', 'الاسم فقط مطلوب للحفظ.')}</p>
          </div>
          {editing && <GlassButton size="sm" onClick={() => {
            setEditing(null);
            setPayload(EMPTY_AUTOMATION);
            setContext(EMPTY_CONTEXT);
          }}>{l(S.cancel)}</GlassButton>}
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          <label className="text-xs font-bold text-ink-soft">
            {text('Draft name', 'اسم المسودة')}
            <GlassInput value={payload.name} onChange={(event) => setPayload({ ...payload, name: event.target.value })} className="mt-1" />
          </label>
          <label className="text-xs font-bold text-ink-soft">
            {text('Intended outcome', 'النتيجة المقصودة')}
            <GlassInput value={payload.outcome ?? ''} onChange={(event) => setPayload({ ...payload, outcome: event.target.value || undefined })} className="mt-1" />
          </label>
          <label className="text-xs font-bold text-ink-soft">
            {text('Trigger in plain language', 'المحفز بلغة طبيعية')}
            <GlassTextarea rows={3} value={payload.trigger ?? ''} onChange={(event) => setPayload({ ...payload, trigger: event.target.value || undefined })} className="mt-1" />
          </label>
          <label className="text-xs font-bold text-ink-soft">
            {text('Action in plain language', 'الإجراء بلغة طبيعية')}
            <GlassTextarea rows={3} value={payload.action ?? ''} onChange={(event) => setPayload({ ...payload, action: event.target.value || undefined })} className="mt-1" />
          </label>
          <label className="text-xs font-bold text-ink-soft lg:col-span-2">
            {text('Additional operating context', 'سياق تشغيلي إضافي')}
            <GlassTextarea rows={3} value={payload.context ?? ''} onChange={(event) => setPayload({ ...payload, context: event.target.value || undefined })} className="mt-1" />
          </label>
        </div>
        <div className="mt-4"><ContextFields value={context} onChange={setContext} /></div>
        <div className="mt-4 rounded-xl border border-aeblack-100 p-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={showMapping} onChange={(event) => setShowMapping(event.target.checked)} />
            {text('Add executable schedule mapping now', 'إضافة ربط جدول قابل للتنفيذ الآن')}
          </label>
          {showMapping && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <label className="text-xs font-bold text-ink-soft sm:col-span-2">
                {l(S.scheduleName)}
                <GlassInput value={schedule.name} onChange={(event) => setSchedule({ ...schedule, name: event.target.value })} className="mt-1" />
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.purpose)}
                <GlassSelect value={schedule.purpose} onChange={(event) => setSchedule({ ...schedule, purpose: event.target.value as AgentScheduleConfig['purpose'] })} className="mt-1">
                  {['agent_run', 'notification', 'digest', 'escalation'].map((purpose) => <option key={purpose}>{purpose}</option>)}
                </GlassSelect>
              </label>
              <label className="flex items-end gap-2 pb-2 text-xs font-bold text-ink-soft">
                <input type="checkbox" checked={schedule.enabled} onChange={(event) => setSchedule({ ...schedule, enabled: event.target.checked })} />
                {l(S.enabled)}
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.cronExpression)}
                <GlassInput value={schedule.cron} onChange={(event) => setSchedule({ ...schedule, cron: event.target.value })} className="mt-1" dir="ltr" />
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.timezone)}
                <GlassInput value={schedule.timezone} onChange={(event) => setSchedule({ ...schedule, timezone: event.target.value })} className="mt-1" dir="ltr" />
              </label>
              <div className="flex items-end">
                <GlassButton size="sm" disabled={previewMutation.isPending || !schedule.cron.trim()} onClick={() => previewMutation.mutate()}>
                  {l(S.preview)}
                </GlassButton>
              </div>
              <div className="sm:col-span-2 xl:col-span-4 flex flex-wrap gap-1">
                {preview.map((occurrence) => <time key={occurrence} className="rounded bg-aeblack-50 px-2 py-1 font-mono text-[10px]">{new Date(occurrence).toLocaleString()}</time>)}
              </div>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.audienceType)}
                <GlassSelect value={schedule.audience.type} onChange={(event) => setSchedule({ ...schedule, audience: { ...schedule.audience, type: event.target.value as AgentScheduleConfig['audience']['type'] } })} className="mt-1">
                  {['all', 'role', 'segment', 'custom'].map((type) => <option key={type}>{type}</option>)}
                </GlassSelect>
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.audienceValues)}
                <GlassInput value={audienceValues} onChange={(event) => setAudienceValues(event.target.value)} className="mt-1" />
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.channel)}
                <GlassSelect value={schedule.channel ?? ''} onChange={(event) => setSchedule({ ...schedule, channel: (event.target.value || null) as AgentScheduleConfig['channel'] })} className="mt-1">
                  <option value="">{l(S.none)}</option>
                  {['email', 'portal', 'whatsapp_template', 'whatsapp_session'].map((channel) => <option key={channel}>{channel}</option>)}
                </GlassSelect>
              </label>
              <label className="text-xs font-bold text-ink-soft">
                {l(S.templateKey)}
                <GlassInput value={schedule.templateKey ?? ''} onChange={(event) => setSchedule({ ...schedule, templateKey: event.target.value || null })} className="mt-1" />
              </label>
              <label className="text-xs font-bold text-ink-soft sm:col-span-2">
                {l(S.conditionsJson)}
                <GlassTextarea rows={5} value={conditionsText} onChange={(event) => setConditionsText(event.target.value)} className="mt-1 font-mono" dir="ltr" />
              </label>
            </div>
          )}
        </div>
        {editing && (
          <div className="mt-4">
            <ResourceGuidance slug={slug} draft={editing} workspace={workspace} selectedNoteIds={selectedNoteIds} onApplied={actions.refresh} />
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <GlassButton variant="primary" disabled={save.isPending || !payload.name.trim()} onClick={() => save.mutate()}>
            {save.isPending ? l(S.saving) : text('Save draft', 'حفظ المسودة')}
          </GlassButton>
        </div>
      </GlassCard>
    </div>
  );
}
